import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './btcTracker.css';

const POPOVER_WIDTH = 360;
const POPOVER_OFFSET = 12;

export default function BTCRailCard({ variant = 'rail' }) {
  const [showWidget, setShowWidget] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const cardRef = useRef(null);
  const { darkTheme } = useTheme();
  const isDock = variant === 'dock';

  const updatePopoverPosition = useCallback(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth || 0;
    const width = Math.min(POPOVER_WIDTH, viewportWidth - 24);
    let left = rect.left;
    if (left + width > viewportWidth - 12) {
      left = viewportWidth - width - 12;
    }
    if (left < 12) left = 12;
    const style = {
      position: 'fixed',
      left,
      width,
      maxWidth: width,
      top: rect.top - POPOVER_OFFSET,
      bottom: 'auto',
      transform: 'translateY(-100%)',
      zIndex: 9999,
    };
    setPopoverStyle(style);
  }, []);

  const openWidget = useCallback(() => {
    updatePopoverPosition();
    setShowWidget(true);
  }, [updatePopoverPosition]);

  useEffect(() => {
    if (!showWidget) return undefined;
    const script = document.createElement('script');
    script.src = 'https://widgets.coingecko.com/gecko-coin-price-chart-widget.js';
    script.async = true;
    document.body.appendChild(script);

    updatePopoverPosition();

    const timerId = setTimeout(() => {
      setShowWidget(false);
    }, 10000);

    return () => {
      document.body.removeChild(script);
      clearTimeout(timerId);
    };
  }, [showWidget, updatePopoverPosition]);

  useEffect(() => {
    if (!showWidget) return undefined;
    const handleUpdate = () => updatePopoverPosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [showWidget, updatePopoverPosition]);

  return (
    <div
      ref={cardRef}
      className={
        isDock
          ? 'btcDockPill'
          : 'feedHighlightCard feedHighlightCard--btc btcRailCard'
      }
      role="button"
      tabIndex={0}
      onClick={openWidget}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          openWidget();
        }
      }}
    >
      <img
        className="btcMiniIcon"
        src="https://bitcoin.org/img/icons/opengraph.png?1660986466"
        alt="BTC"
        loading="lazy"
      />
      <div>
        <div className={isDock ? 'btcDockLabel' : 'feedHighlightTitle'}>
          BTC
        </div>
        {!isDock && <div className="feedHighlightMeta">Tap for chart</div>}
      </div>
      {showWidget && (
        <div
          className="btcRailPopover"
          style={popoverStyle || undefined}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="btcRailPopoverHeader">
            <span>Bitcoin (BTC)</span>
            <button
              type="button"
              className="btcRailPopoverClose"
              onClick={() => setShowWidget(false)}
            >
              Close
            </button>
          </div>
          <gecko-coin-price-chart-widget
            locale="en"
            outlined="true"
            initial-currency="usd"
            dark-mode={darkTheme ? true : false}
          ></gecko-coin-price-chart-widget>
        </div>
      )}
    </div>
  );
}
